import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  CircleDashed,
  Clock3,
  Database,
  FileArchive,
  Loader2,
  PlugZap,
  RefreshCw,
  Save,
  ServerCog,
  Trash2,
  XCircle,
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { copyServiceHealthSchedulePayload } from "@/lib/copy-service-health";
import { getSupabaseFunctionErrorMessage } from "@/lib/supabase-function-errors";
import { cn } from "@/lib/utils";
import { DebugAppClone } from "./DebugAppClone";

type ServiceConfiguration = {
  endpoint: string;
  username: string;
  password_configured: boolean;
  updated_at: string | null;
};

type LegacyHealthSlot = {
  time: string;
  enabled: boolean;
  positive_enabled: boolean;
  last_run_date: string | null;
};

type HealthRunStatus = "running" | "success" | "failure" | "skipped";
type HealthSlackStatus = "not_required" | "pending" | "posted" | "failed";

type HealthRun = {
  id: string;
  trigger: "manual" | "scheduled" | null;
  slot_index: number | null;
  slot_time: string | null;
  scheduled_for: string | null;
  status: HealthRunStatus;
  failure_reasons: string[];
  failure_category: string | null;
  sanitized_message: string | null;
  error_message: string | null;
  slack_status: HealthSlackStatus;
  slack_error: string | null;
  started_at: string | null;
  completed_at: string | null;
  duration_ms: number | null;
  created_at: string | null;
};

type WatchdogSlot = {
  slot_index: number;
  slot_hour_utc: number;
  slot_utc_label: string;
  check_enabled: boolean;
  positive_enabled: boolean;
  updated_at: string | null;
  last_run: HealthRun | null;
  recent_runs: HealthRun[];
};

type WatchdogPayload =
  | {
    contract: "watchdog_v1";
    ok: true;
    generated_at: string;
    slots: WatchdogSlot[];
  }
  | {
    contract: "legacy_schedule";
    generated_at: null;
    slots: LegacyHealthSlot[];
  };

type CopyJob = {
  job_id: string;
  type: "template_export" | "instance_create" | "debug_app_clone" | "debug_clone";
  status: "queued" | "running" | "succeeded" | "failed";
  phase: string;
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
  retry_after_seconds: number | null;
  error?: { code?: string; message?: string };
  template_id?: string;
  instance_created?: boolean;
};

type CopyTemplate = {
  template_id: string;
  display_name: string;
  client_reference: string | null;
  created_at: string;
  status: string;
  digest: string;
  file_count: number;
  entity_count: number;
  entity_rows: number;
  size_bytes: number;
};

type Pagination = { limit: number; offset: number; total: number };

const DEFAULT_LEGACY_SLOTS: LegacyHealthSlot[] = ["00:00", "04:00", "08:00", "12:00", "16:00", "20:00"]
  .map((time) => ({
    time,
    enabled: true,
    positive_enabled: false,
    last_run_date: null,
  }));
const PAGE_SIZE = 10;

function formatDate(value?: string | null): string {
  if (!value) return "Not yet";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Unknown";
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 ** 2) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 ** 2).toFixed(1)} MB`;
}

function localSlotLabel(time: string): string {
  const [hour, minute] = time.split(":").map(Number);
  const now = new Date();
  const date = new Date(Date.UTC(
    now.getUTCFullYear(),
    now.getUTCMonth(),
    now.getUTCDate(),
    hour,
    minute,
  ));
  return date.toLocaleString(undefined, {
    weekday: "short",
    hour: "2-digit",
    minute: "2-digit",
    timeZoneName: "short",
  });
}

function statusClass(status: string): string {
  if (status === "succeeded" || status === "success") return "border-emerald-500/40 bg-emerald-500/10 text-emerald-700";
  if (status === "failed" || status === "failure") return "border-destructive/40 bg-destructive/10 text-destructive";
  if (status === "running") return "border-blue-500/40 bg-blue-500/10 text-blue-700";
  if (status === "skipped") return "border-border bg-muted text-muted-foreground";
  return "border-border bg-background text-muted-foreground";
}

function statusLabel(status: string): string {
  return status === "succeeded"
    ? "Succeeded"
    : status === "failed"
      ? "Failed"
      : status === "running"
        ? "Running"
        : "Queued";
}

function StatusIcon({ status }: { status: HealthRunStatus }) {
  if (status === "success") return <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />;
  if (status === "failure") return <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" />;
  if (status === "running") return <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />;
  return <CircleDashed className="h-3.5 w-3.5" aria-hidden="true" />;
}

function slackLabel(status?: string | null): string {
  if (status === "posted") return "Slack posted";
  if (status === "failed") return "Slack failed";
  if (status === "pending") return "Slack pending";
  return "No Slack";
}

function slackStateClass(status?: string | null): string {
  if (status === "failed") return "font-medium text-destructive";
  if (status === "posted") return "font-medium text-emerald-700";
  return "text-muted-foreground";
}

function healthStatusLabel(status: HealthRunStatus): string {
  if (status === "success") return "Passed";
  if (status === "failure") return "Failed";
  if (status === "skipped") return "Skipped";
  return "Running";
}

function healthIssue(run?: HealthRun | null): string | null {
  if (!run) return null;
  const issue = run.failure_reasons[0]
    || run.sanitized_message
    || run.failure_category?.replaceAll("_", " ")
    || run.error_message;
  return issue?.trim() || null;
}

function formatDuration(value?: number | null): string {
  if (typeof value !== "number" || !Number.isFinite(value) || value < 0) return "—";
  if (value < 1000) return `${Math.round(value)} ms`;
  return `${(value / 1000).toFixed(value < 10_000 ? 1 : 0)} s`;
}

function resultLabel(job: CopyJob): string {
  if (job.status !== "succeeded") return "—";
  if (job.template_id) return job.template_id;
  if (job.instance_created) return "Instance ready";
  return "Completed";
}

function jobTypeLabel(job: CopyJob): string {
  if (job.type === "template_export") return "Template export";
  if (job.type === "debug_app_clone" || job.type === "debug_clone") return "Debug App Clone";
  return "Instance creation";
}

class CopyServiceAdminRequestError extends Error {
  code: string;

  constructor(code: string, message: string) {
    super(message);
    this.name = "CopyServiceAdminRequestError";
    this.code = code;
  }
}

async function responseErrorCode(error: unknown): Promise<string | null> {
  const context = error && typeof error === "object"
    ? (error as { context?: unknown }).context
    : null;
  if (!context || typeof context !== "object") return null;
  try {
    const response = typeof (context as Response).clone === "function"
      ? (context as Response).clone()
      : context as Response;
    const payload = await response.json() as { error?: { code?: unknown } };
    return typeof payload?.error?.code === "string" ? payload.error.code : null;
  } catch {
    return null;
  }
}

export function CopyServiceAdmin() {
  const { toast } = useToast();
  const [configuration, setConfiguration] = useState<ServiceConfiguration>({
    endpoint: "",
    username: "",
    password_configured: false,
    updated_at: null,
  });
  const [password, setPassword] = useState("");
  const [watchdogContract, setWatchdogContract] = useState<WatchdogPayload["contract"] | null>(null);
  const [watchdogSlots, setWatchdogSlots] = useState<WatchdogSlot[]>([]);
  const [legacySlots, setLegacySlots] = useState<LegacyHealthSlot[]>(DEFAULT_LEGACY_SLOTS);
  const [manualChecks, setManualChecks] = useState<HealthRun[]>([]);
  const [jobs, setJobs] = useState<CopyJob[]>([]);
  const [templates, setTemplates] = useState<CopyTemplate[]>([]);
  const [jobPage, setJobPage] = useState<Pagination>({ limit: PAGE_SIZE, offset: 0, total: 0 });
  const [templatePage, setTemplatePage] = useState<Pagination>({ limit: PAGE_SIZE, offset: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [watchdogLoading, setWatchdogLoading] = useState(false);
  const [watchdogError, setWatchdogError] = useState<string | null>(null);
  const [manualLoading, setManualLoading] = useState(false);
  const [manualError, setManualError] = useState<string | null>(null);
  const [manualRetryAction, setManualRetryAction] = useState<"load" | "run" | null>(null);
  const [savingConfig, setSavingConfig] = useState(false);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [savingLegacySchedule, setSavingLegacySchedule] = useState(false);
  const [pinging, setPinging] = useState(false);
  const [runningCheck, setRunningCheck] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<CopyTemplate | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<"unknown" | "connected" | "failed">("unknown");

  const timeZone = useMemo(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone || "Local time",
    [],
  );

  const invoke = useCallback(async <T,>(action: string, body: Record<string, unknown> = {}): Promise<T> => {
    const { data, error } = await supabase.functions.invoke("copy-service-admin", {
      body: { action, ...body },
    });
    const apiError = data?.error;
    if (error || apiError) {
      const code = typeof apiError?.code === "string"
        ? apiError.code
        : await responseErrorCode(error);
      const message = typeof apiError?.message === "string"
        ? apiError.message
        : await getSupabaseFunctionErrorMessage(error, "CopyService request failed.");
      throw new CopyServiceAdminRequestError(code ?? "copy_service_error", message);
    }
    return data as T;
  }, []);

  const loadOperations = useCallback(async (
    jobOffset = jobPage.offset,
    templateOffset = templatePage.offset,
  ) => {
    const [jobsData, templatesData] = await Promise.all([
      invoke<{ jobs: CopyJob[]; pagination: Pagination }>("list_jobs", {
        limit: PAGE_SIZE,
        offset: jobOffset,
      }),
      invoke<{ templates: CopyTemplate[]; pagination: Pagination }>("list_templates", {
        limit: PAGE_SIZE,
        offset: templateOffset,
      }),
    ]);
    setJobs(jobsData.jobs);
    setJobPage(jobsData.pagination);
    setTemplates(templatesData.templates);
    setTemplatePage(templatesData.pagination);
  }, [invoke, jobPage.offset, templatePage.offset]);

  const loadWatchdog = useCallback(async () => {
    setWatchdogLoading(true);
    setWatchdogError(null);
    try {
      const data = await invoke<WatchdogPayload>("get_health_watchdog");
      setWatchdogContract(data.contract);
      if (data.contract === "watchdog_v1") {
        setWatchdogSlots(data.slots);
      } else {
        setLegacySlots(data.slots);
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : "CopyService Watchdog did not load.";
      setWatchdogError(message);
      throw error;
    } finally {
      setWatchdogLoading(false);
    }
  }, [invoke]);

  const loadManualChecks = useCallback(async () => {
    setManualLoading(true);
    setManualError(null);
    setManualRetryAction(null);
    try {
      const data = await invoke<{ checks: HealthRun[] }>("list_health_checks", {
        trigger: "manual",
        limit: 20,
        offset: 0,
      });
      setManualChecks(data.checks);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Manual checks did not load.";
      setManualError(message);
      setManualRetryAction("load");
      throw error;
    } finally {
      setManualLoading(false);
    }
  }, [invoke]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await invoke<{ configuration: ServiceConfiguration }>("get_config");
      setConfiguration(data.configuration);
      if (data.configuration.password_configured) {
        void Promise.allSettled([
          loadOperations(0, 0),
          loadWatchdog(),
          loadManualChecks(),
        ]);
      }
    } catch (error) {
      toast({
        title: "Unable to load CopyService",
        description: error instanceof Error ? error.message : "Try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [invoke, loadManualChecks, loadOperations, loadWatchdog, toast]);

  useEffect(() => {
    void load();
    // Load only when this admin tab mounts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refresh = async () => {
    if (!configuration.password_configured) return;
    setRefreshing(true);
    const results = await Promise.allSettled([
      loadOperations(),
      loadWatchdog(),
      loadManualChecks(),
    ]);
    try {
      if (results.some((result) => result.status === "rejected")) {
        throw new Error("Unavailable sections show their own retry message.");
      }
      toast({ title: "CopyService data refreshed" });
    } catch (error) {
      toast({
        title: "Unable to refresh CopyService",
        description: error instanceof Error ? error.message : "Try again.",
        variant: "destructive",
      });
    } finally {
      setRefreshing(false);
    }
  };

  const saveConfiguration = async () => {
    if (!configuration.endpoint.trim() || !configuration.username.trim()) {
      toast({
        title: "Endpoint and username required",
        description: "Enter both values before saving.",
        variant: "destructive",
      });
      return;
    }
    if (!configuration.password_configured && !password) {
      toast({
        title: "Password required",
        description: "Enter the service password for the initial setup.",
        variant: "destructive",
      });
      return;
    }
    setSavingConfig(true);
    try {
      await invoke("save_config", {
        endpoint: configuration.endpoint,
        username: configuration.username,
        password: password || undefined,
      });
      setPassword("");
      setConfiguration((current) => ({ ...current, password_configured: true, updated_at: new Date().toISOString() }));
      setConnectionStatus("unknown");
      void Promise.allSettled([
        loadOperations(0, 0),
        loadWatchdog(),
        loadManualChecks(),
      ]);
      toast({
        title: "Connection settings saved",
        description: "The password is encrypted and will not be shown again.",
      });
    } catch (error) {
      toast({
        title: "Unable to save settings",
        description: error instanceof Error ? error.message : "Try again.",
        variant: "destructive",
      });
    } finally {
      setSavingConfig(false);
    }
  };

  const ping = async () => {
    setPinging(true);
    try {
      await invoke("ping");
      setConnectionStatus("connected");
      toast({ title: "CopyService is reachable" });
      await Promise.allSettled([
        loadOperations(0, 0),
        loadWatchdog(),
        loadManualChecks(),
      ]);
    } catch (error) {
      setConnectionStatus("failed");
      toast({
        title: "CopyService did not respond",
        description: error instanceof Error ? error.message : "Check the endpoint and credentials.",
        variant: "destructive",
      });
    } finally {
      setPinging(false);
    }
  };

  const saveWatchdogSlot = async (
    slot: WatchdogSlot,
    patch: { check_enabled?: boolean; positive_enabled?: boolean },
  ) => {
    if (savingKey) return;
    const previous = watchdogSlots;
    const checkEnabled = patch.check_enabled ?? slot.check_enabled;
    const positiveEnabled = checkEnabled
      ? (patch.positive_enabled ?? slot.positive_enabled)
      : false;
    const key = `${slot.slot_index}:${Object.keys(patch)[0]}`;
    setWatchdogSlots((current) => current.map((candidate) => (
      candidate.slot_index === slot.slot_index
        ? {
          ...candidate,
          check_enabled: checkEnabled,
          positive_enabled: positiveEnabled,
        }
        : candidate
    )));
    setSavingKey(key);
    setWatchdogError(null);
    try {
      const data = await invoke<WatchdogPayload>("save_health_watchdog_slot", {
        slot_index: slot.slot_index,
        check_enabled: checkEnabled,
        positive_enabled: positiveEnabled,
      });
      if (data.contract !== "watchdog_v1") {
        throw new Error("CopyService returned an incompatible Watchdog response.");
      }
      setWatchdogSlots(data.slots);
      toast({ title: "CopyService check updated" });
    } catch (error) {
      setWatchdogSlots(previous);
      const message = error instanceof Error ? error.message : "Try again.";
      setWatchdogError(message);
      toast({
        title: "Unable to update health check",
        description: message,
        variant: "destructive",
      });
    } finally {
      setSavingKey(null);
    }
  };

  const saveLegacySchedule = async () => {
    setSavingLegacySchedule(true);
    setWatchdogError(null);
    try {
      const data = await invoke<WatchdogPayload>("save_health_schedule", {
        slots: copyServiceHealthSchedulePayload(legacySlots),
      });
      if (data.contract !== "legacy_schedule") {
        throw new Error("CopyService returned an incompatible legacy schedule.");
      }
      setLegacySlots(data.slots);
      toast({ title: "Legacy health schedule saved" });
    } catch (error) {
      const message = error instanceof Error ? error.message : "Try again.";
      setWatchdogError(message);
      toast({
        title: "Unable to save legacy schedule",
        description: message,
        variant: "destructive",
      });
    } finally {
      setSavingLegacySchedule(false);
    }
  };

  const runHealthCheck = async () => {
    setRunningCheck(true);
    setManualError(null);
    setManualRetryAction(null);
    try {
      const data = await invoke<{ check: HealthRun }>("run_health_check");
      setManualChecks((current) => [data.check, ...current.filter((check) => check.id !== data.check.id)].slice(0, 20));
      toast({
        title: data.check.status === "success"
          ? "Health check passed"
          : data.check.status === "failure"
            ? "Health check failed"
            : data.check.status === "skipped"
              ? "Health check skipped"
              : "Health check started",
        variant: data.check.status === "failure" ? "destructive" : "default",
      });
    } catch (error) {
      const busy = error instanceof CopyServiceAdminRequestError && error.code === "health_check_busy";
      const message = busy
        ? "Provider work is in progress. Wait for the current App operation to finish, then try again."
        : error instanceof Error
          ? error.message
          : "Try again.";
      setManualError(message);
      setManualRetryAction("run");
      toast({
        title: busy ? "Provider work is in progress" : "Unable to run health check",
        description: message,
        variant: "destructive",
      });
    } finally {
      setRunningCheck(false);
    }
  };

  const loadJobPage = async (offset: number) => {
    try {
      const data = await invoke<{ jobs: CopyJob[]; pagination: Pagination }>("list_jobs", {
        limit: PAGE_SIZE,
        offset,
      });
      setJobs(data.jobs);
      setJobPage(data.pagination);
    } catch (error) {
      toast({ title: "Unable to load jobs", description: error instanceof Error ? error.message : "Try again.", variant: "destructive" });
    }
  };

  const loadTemplatePage = async (offset: number) => {
    try {
      const data = await invoke<{ templates: CopyTemplate[]; pagination: Pagination }>("list_templates", {
        limit: PAGE_SIZE,
        offset,
      });
      setTemplates(data.templates);
      setTemplatePage(data.pagination);
    } catch (error) {
      toast({ title: "Unable to load templates", description: error instanceof Error ? error.message : "Try again.", variant: "destructive" });
    }
  };

  const deleteTemplate = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await invoke("delete_template", { template_id: deleteTarget.template_id });
      setDeleteTarget(null);
      await loadTemplatePage(Math.max(0, templatePage.offset - (templates.length === 1 ? PAGE_SIZE : 0)));
      toast({
        title: "Template deleted",
        description: "Its source and created instances were not changed.",
      });
    } catch (error) {
      toast({
        title: "Unable to delete template",
        description: error instanceof Error ? error.message : "Try again.",
        variant: "destructive",
      });
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-5" aria-busy="true" aria-label="Loading CopyService administration">
        <Skeleton className="h-8 w-52" />
        <Skeleton className="h-64 w-full" />
        <Skeleton className="h-80 w-full" />
      </div>
    );
  }

  const configured = configuration.password_configured;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="flex items-center gap-2 text-xl font-semibold text-foreground">
              <ServerCog className="h-5 w-5" />
              CopyService
            </h2>
            {connectionStatus === "connected" && (
              <Badge variant="outline" className="border-emerald-500/40 bg-emerald-500/10 text-emerald-700">
                <CheckCircle2 className="mr-1 h-3.5 w-3.5" /> Connected
              </Badge>
            )}
            {connectionStatus === "failed" && (
              <Badge variant="outline" className="border-destructive/40 bg-destructive/10 text-destructive">
                <XCircle className="mr-1 h-3.5 w-3.5" /> Needs attention
              </Badge>
            )}
          </div>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            Configure the template service, monitor durable work, and manage archived templates.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => void refresh()}
          disabled={
            !configured
            || refreshing
            || Boolean(savingKey)
            || savingLegacySchedule
            || runningCheck
          }
        >
          {refreshing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
          Refresh
        </Button>
      </div>

      <Card className="border-border/60">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <PlugZap className="h-4 w-4" />
            Connection
          </CardTitle>
          <CardDescription>
            The password is write-only. Saving an empty password keeps the existing credential.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(12rem,1fr)]">
            <div className="space-y-2">
              <Label htmlFor="copy-service-endpoint">HTTPS endpoint</Label>
              <Input
                id="copy-service-endpoint"
                type="url"
                inputMode="url"
                autoComplete="url"
                placeholder="https://copy-service.example.ngrok-free.app"
                value={configuration.endpoint}
                onChange={(event) => setConfiguration((current) => ({ ...current, endpoint: event.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="copy-service-username">Service user</Label>
              <Input
                id="copy-service-username"
                autoComplete="username"
                placeholder="applyt-dev"
                value={configuration.username}
                onChange={(event) => setConfiguration((current) => ({ ...current, username: event.target.value }))}
              />
            </div>
          </div>
          <div className="space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <Label htmlFor="copy-service-password">
                {configured ? "New password (optional)" : "Password"}
              </Label>
              <span className="text-xs text-muted-foreground">
                {configured ? "Password configured" : "No password configured"}
              </span>
            </div>
            <Input
              id="copy-service-password"
              type="password"
              autoComplete="new-password"
              placeholder={configured ? "Leave blank to keep the current password" : "At least 16 characters"}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
          </div>
          <div className="flex flex-col gap-2 sm:flex-row">
            <Button onClick={() => void saveConfiguration()} disabled={savingConfig}>
              {savingConfig ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
              Save settings
            </Button>
            <Button
              variant="outline"
              onClick={() => void ping()}
              disabled={
                !configured
                || pinging
                || refreshing
                || Boolean(savingKey)
                || savingLegacySchedule
                || runningCheck
              }
            >
              {pinging ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Activity className="mr-2 h-4 w-4" />}
              Ping service
            </Button>
          </div>
        </CardContent>
      </Card>

      {!configured ? (
        <Card className="border-dashed">
          <CardContent className="p-8 text-center">
            <Database className="mx-auto h-8 w-8 text-muted-foreground" />
            <h3 className="mt-3 font-medium">Connect CopyService to continue</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Jobs, templates, and health controls become available after the initial settings are saved.
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          <DebugAppClone />
          {watchdogContract === "legacy_schedule" ? (
            <Card
              className="border-border/60"
              aria-busy={watchdogLoading || savingLegacySchedule}
            >
              <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Clock3 className="h-4 w-4" aria-hidden="true" />
                    Legacy health schedule
                  </CardTitle>
                  <CardDescription>
                    Six UTC slots from the compatibility API. Local zone: {timeZone}.
                  </CardDescription>
                </div>
                <Button size="sm" onClick={() => void saveLegacySchedule()} disabled={savingLegacySchedule || watchdogLoading}>
                  {savingLegacySchedule ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                  Save schedule
                </Button>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-3 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-900">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                  <div>
                    <p className="font-medium">Compatibility mode</p>
                    <p className="mt-1 text-amber-900/80">
                      CopyService has not switched to Watchdog v1. This six-slot fallback remains for one compatibility release.
                    </p>
                  </div>
                </div>
                {watchdogError && (
                  <div
                    className="flex flex-col gap-3 rounded-lg border border-destructive/40 bg-destructive/10 p-3 text-sm sm:flex-row sm:items-center sm:justify-between"
                    role="alert"
                  >
                    <span className="text-destructive">{watchdogError}</span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => void loadWatchdog().catch(() => undefined)}
                      disabled={watchdogLoading}
                    >
                      Retry
                    </Button>
                  </div>
                )}
                <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                  {legacySlots.map((slot, index) => (
                    <div key={slot.time} className="flex items-center justify-between gap-4 rounded-lg border border-border/60 p-3">
                      <div>
                        <Label htmlFor={`copy-health-${slot.time}`} className="font-medium">{slot.time} UTC</Label>
                        <p className="mt-1 text-xs text-muted-foreground">{localSlotLabel(slot.time)}</p>
                        <p className="mt-1 text-xs text-muted-foreground">
                          Last run: {slot.last_run_date ?? "Not yet"}
                        </p>
                      </div>
                      <Switch
                        id={`copy-health-${slot.time}`}
                        checked={slot.enabled}
                        disabled={savingLegacySchedule || watchdogLoading}
                        onCheckedChange={(enabled) => setLegacySlots((current) => current.map((candidate, candidateIndex) => (
                          candidateIndex === index ? { ...candidate, enabled } : candidate
                        )))}
                        aria-label={`${slot.enabled ? "Disable" : "Enable"} ${slot.time} UTC legacy health check`}
                      />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card
              className="border-border/60"
              aria-busy={watchdogLoading || Boolean(savingKey)}
            >
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Clock3 className="h-4 w-4" aria-hidden="true" />
                  CopyService Watchdog
                </CardTitle>
                <CardDescription id="copy-watchdog-description">
                  Checkboxes save immediately. Failures always alert Slack; Positive also reports successful checks.
                  {" "}Local zone: {timeZone}.
                </CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                {watchdogError && (
                  <div
                    className="mx-4 mb-4 flex flex-col gap-3 rounded-lg border border-destructive/40 bg-destructive/10 p-3 text-sm sm:flex-row sm:items-center sm:justify-between"
                    role="alert"
                  >
                    <span className="text-destructive">{watchdogError}</span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => void loadWatchdog().catch(() => undefined)}
                      disabled={watchdogLoading}
                    >
                      Retry
                    </Button>
                  </div>
                )}
                <Table aria-label="CopyService Watchdog schedule and recent history">
                  <TableCaption className="sr-only">
                    Eight fixed UTC Watchdog slots with Check, Positive, latest result, and four recent runs.
                  </TableCaption>
                  <TableHeader>
                    <TableRow>
                      <TableHead scope="col" className="min-w-44">Slot</TableHead>
                      <TableHead scope="col" className="w-28 text-center">Check</TableHead>
                      <TableHead scope="col" className="w-32 text-center">Positive</TableHead>
                      <TableHead scope="col" className="min-w-64">Last Run</TableHead>
                      <TableHead scope="col" className="min-w-56">Recent History</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {watchdogLoading && watchdogSlots.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} className="h-36 text-center text-sm text-muted-foreground">
                          <Loader2 className="mr-2 inline h-4 w-4 animate-spin" />
                          Loading Watchdog...
                        </TableCell>
                      </TableRow>
                    )}
                    {!watchdogLoading && !watchdogError && watchdogSlots.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} className="h-36 text-center text-sm text-muted-foreground">
                          No CopyService Watchdog slots found.
                        </TableCell>
                      </TableRow>
                    )}
                    {watchdogSlots.map((slot) => {
                      const lastRun = slot.last_run;
                      const issue = healthIssue(lastRun);
                      const checkSaving = savingKey === `${slot.slot_index}:check_enabled`;
                      const positiveSaving = savingKey === `${slot.slot_index}:positive_enabled`;
                      const slotTime = `${String(slot.slot_hour_utc).padStart(2, "0")}:00`;
                      return (
                        <TableRow key={slot.slot_index}>
                          <TableCell>
                            <div className="font-medium text-foreground">{localSlotLabel(slotTime)}</div>
                            <div className="mt-1 text-xs text-muted-foreground">{slot.slot_utc_label}</div>
                          </TableCell>
                          <TableCell className="text-center">
                            <Checkbox
                              aria-label={`Enable ${slot.slot_utc_label} CopyService check`}
                              aria-describedby="copy-watchdog-description"
                              checked={slot.check_enabled}
                              disabled={Boolean(savingKey) || watchdogLoading}
                              onCheckedChange={(checked) => void saveWatchdogSlot(slot, {
                                check_enabled: checked === true,
                              })}
                            />
                            {checkSaving && (
                              <>
                                <Loader2 className="ml-2 inline h-3.5 w-3.5 animate-spin text-muted-foreground" aria-hidden="true" />
                                <span className="sr-only" role="status">
                                  Saving Check setting for {slot.slot_utc_label}
                                </span>
                              </>
                            )}
                          </TableCell>
                          <TableCell className="text-center">
                            <Checkbox
                              aria-label={`Post successful ${slot.slot_utc_label} CopyService check`}
                              aria-describedby="copy-watchdog-description"
                              checked={slot.check_enabled && slot.positive_enabled}
                              disabled={!slot.check_enabled || Boolean(savingKey) || watchdogLoading}
                              onCheckedChange={(checked) => void saveWatchdogSlot(slot, {
                                positive_enabled: checked === true,
                              })}
                            />
                            {positiveSaving && (
                              <>
                                <Loader2 className="ml-2 inline h-3.5 w-3.5 animate-spin text-muted-foreground" aria-hidden="true" />
                                <span className="sr-only" role="status">
                                  Saving Positive setting for {slot.slot_utc_label}
                                </span>
                              </>
                            )}
                          </TableCell>
                          <TableCell>
                            {lastRun ? (
                              <div className="space-y-2">
                                <div className="flex flex-wrap items-center gap-2">
                                  <Badge variant="outline" className={cn("gap-1", statusClass(lastRun.status))}>
                                    <StatusIcon status={lastRun.status} />
                                    {healthStatusLabel(lastRun.status)}
                                  </Badge>
                                  <span className="text-xs text-muted-foreground">
                                    {formatDate(lastRun.scheduled_for)}
                                  </span>
                                </div>
                                <div className="text-xs text-muted-foreground">
                                  {formatDuration(lastRun.duration_ms)} ·{" "}
                                  <span className={slackStateClass(lastRun.slack_status)}>
                                    {slackLabel(lastRun.slack_status)}
                                  </span>
                                  {lastRun.slack_error ? `: ${lastRun.slack_error}` : ""}
                                </div>
                                {issue && (
                                  <div className="max-w-md text-xs text-muted-foreground line-clamp-2">
                                    {issue}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <span className="text-sm text-muted-foreground">Not run</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {slot.recent_runs.length > 0 ? (
                              <div className="space-y-1.5">
                                {slot.recent_runs.slice(0, 4).map((run) => (
                                  <div key={run.id} className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
                                    <Badge
                                      variant="outline"
                                      className={cn("min-w-20 justify-center", statusClass(run.status))}
                                    >
                                      {healthStatusLabel(run.status)}
                                    </Badge>
                                    <span className="text-muted-foreground">{formatDate(run.scheduled_for)}</span>
                                    <span className={slackStateClass(run.slack_status)}>
                                      {slackLabel(run.slack_status)}
                                      {run.slack_error ? `: ${run.slack_error}` : ""}
                                    </span>
                                    {healthIssue(run) && (
                                      <span
                                        className="text-muted-foreground"
                                        title={healthIssue(run) ?? undefined}
                                      >
                                        Issue recorded
                                      </span>
                                    )}
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <span className="text-sm text-muted-foreground">No history</span>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          <Card className="border-border/60" aria-busy={manualLoading || runningCheck}>
            <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Activity className="h-4 w-4" aria-hidden="true" />
                  Manual health checks
                </CardTitle>
                <CardDescription>
                  Manual checks never post to Slack and stay separate from scheduled Watchdog history.
                </CardDescription>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => void runHealthCheck()}
                disabled={runningCheck || manualLoading || refreshing}
              >
                {runningCheck ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Activity className="mr-2 h-4 w-4" />}
                Run manual check
              </Button>
            </CardHeader>
            <CardContent className="p-0">
              {manualError && (
                <div
                  className="mx-4 mb-4 flex flex-col gap-3 rounded-lg border border-destructive/40 bg-destructive/10 p-3 text-sm sm:flex-row sm:items-center sm:justify-between"
                  role="alert"
                >
                  <span className="text-destructive">{manualError}</span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      if (manualRetryAction === "run") {
                        void runHealthCheck();
                      } else {
                        void loadManualChecks().catch(() => undefined);
                      }
                    }}
                    disabled={manualLoading || runningCheck}
                  >
                    {manualRetryAction === "run" ? "Try check again" : "Retry"}
                  </Button>
                </div>
              )}
              <Table aria-label="Recent manual CopyService health checks">
                <TableCaption className="sr-only">
                  Recent manually requested checks with result, start time, duration, Slack state, and sanitized issue.
                </TableCaption>
                <TableHeader>
                  <TableRow>
                    <TableHead scope="col">Result</TableHead>
                    <TableHead scope="col">Started</TableHead>
                    <TableHead scope="col">Duration</TableHead>
                    <TableHead scope="col">Slack</TableHead>
                    <TableHead scope="col" className="min-w-64">Issue</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {manualLoading && manualChecks.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} className="h-28 text-center text-sm text-muted-foreground">
                        <Loader2 className="mr-2 inline h-4 w-4 animate-spin" />
                        Loading manual checks...
                      </TableCell>
                    </TableRow>
                  )}
                  {!manualLoading && !manualError && manualChecks.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} className="h-28 text-center text-sm text-muted-foreground">
                        No manual health checks have run yet.
                      </TableCell>
                    </TableRow>
                  )}
                  {manualChecks.slice(0, 6).map((check) => (
                    <TableRow key={check.id}>
                      <TableCell>
                        <Badge variant="outline" className={cn("gap-1", statusClass(check.status))}>
                          <StatusIcon status={check.status} />
                          {healthStatusLabel(check.status)}
                        </Badge>
                      </TableCell>
                      <TableCell>{formatDate(check.started_at)}</TableCell>
                      <TableCell>{formatDuration(check.duration_ms)}</TableCell>
                      <TableCell>
                        {slackLabel(check.slack_status)}
                        {check.slack_error ? `: ${check.slack_error}` : ""}
                      </TableCell>
                      <TableCell className="max-w-md text-xs text-muted-foreground">
                        {healthIssue(check) ?? "—"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          <Card className="border-border/60">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Database className="h-4 w-4" />
                Durable jobs
              </CardTitle>
              <CardDescription>A durable queue processes App work with up to two copies in parallel.</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              {jobs.length === 0 ? (
                <div className="p-8 text-center text-sm text-muted-foreground">No jobs for this service user.</div>
              ) : (
                <>
                  <div className="hidden md:block">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Type</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Phase</TableHead>
                          <TableHead>Created</TableHead>
                          <TableHead>Result</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {jobs.map((job) => (
                          <TableRow key={job.job_id}>
                            <TableCell>{jobTypeLabel(job)}</TableCell>
                            <TableCell><Badge variant="outline" className={cn(statusClass(job.status))}>{statusLabel(job.status)}</Badge></TableCell>
                            <TableCell className="capitalize">{job.phase.replaceAll("_", " ")}</TableCell>
                            <TableCell>{formatDate(job.created_at)}</TableCell>
                            <TableCell className="max-w-64 break-all text-xs">
                              {job.error?.message ?? resultLabel(job)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  <div className="grid gap-3 p-4 md:hidden">
                    {jobs.map((job) => (
                      <div key={job.job_id} className="rounded-lg border border-border/60 p-3">
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-medium">{jobTypeLabel(job)}</span>
                          <Badge variant="outline" className={cn(statusClass(job.status))}>{statusLabel(job.status)}</Badge>
                        </div>
                        <p className="mt-2 text-xs capitalize text-muted-foreground">{job.phase.replaceAll("_", " ")} · {formatDate(job.created_at)}</p>
                        <p className="mt-2 break-all text-xs">{job.error?.message ?? resultLabel(job)}</p>
                      </div>
                    ))}
                  </div>
                </>
              )}
              <PaginationControls
                page={jobPage}
                onPrevious={() => void loadJobPage(Math.max(0, jobPage.offset - PAGE_SIZE))}
                onNext={() => void loadJobPage(jobPage.offset + PAGE_SIZE)}
              />
            </CardContent>
          </Card>

          <Card className="border-border/60">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <FileArchive className="h-4 w-4" />
                Templates
              </CardTitle>
              <CardDescription>Immutable archives are scoped to this service user and remain until explicitly deleted.</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              {templates.length === 0 ? (
                <div className="p-8 text-center text-sm text-muted-foreground">No active templates for this service user.</div>
              ) : (
                <>
                  <div className="hidden md:block">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Name</TableHead>
                          <TableHead>Created</TableHead>
                          <TableHead>Entities</TableHead>
                          <TableHead>Size</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead className="w-16"><span className="sr-only">Actions</span></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {templates.map((template) => (
                          <TableRow key={template.template_id}>
                            <TableCell>
                              <p className="font-medium">{template.display_name}</p>
                              <p className="mt-1 max-w-72 break-all font-mono text-xs text-muted-foreground">{template.template_id}</p>
                            </TableCell>
                            <TableCell>{formatDate(template.created_at)}</TableCell>
                            <TableCell>{template.entity_count} types · {template.entity_rows.toLocaleString()} rows</TableCell>
                            <TableCell>{formatSize(template.size_bytes)}</TableCell>
                            <TableCell><Badge variant="outline" className="capitalize">{template.status}</Badge></TableCell>
                            <TableCell>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => setDeleteTarget(template)}
                                aria-label={`Delete template ${template.display_name}`}
                              >
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  <div className="grid gap-3 p-4 md:hidden">
                    {templates.map((template) => (
                      <div key={template.template_id} className="rounded-lg border border-border/60 p-3">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="font-medium">{template.display_name}</p>
                            <p className="mt-1 break-all font-mono text-xs text-muted-foreground">{template.template_id}</p>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setDeleteTarget(template)}
                            aria-label={`Delete template ${template.display_name}`}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                        <p className="mt-3 text-xs text-muted-foreground">
                          {template.entity_count} entity types · {template.entity_rows.toLocaleString()} rows · {formatSize(template.size_bytes)}
                        </p>
                      </div>
                    ))}
                  </div>
                </>
              )}
              <PaginationControls
                page={templatePage}
                onPrevious={() => void loadTemplatePage(Math.max(0, templatePage.offset - PAGE_SIZE))}
                onNext={() => void loadTemplatePage(templatePage.offset + PAGE_SIZE)}
              />
            </CardContent>
          </Card>
        </>
      )}

      <AlertDialog open={Boolean(deleteTarget)} onOpenChange={(open) => !open && !deleting && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this template archive?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget
                ? `“${deleteTarget.display_name}” will be permanently removed from CopyService. Its original source and any instances created from it will remain unchanged.`
                : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Keep template</AlertDialogCancel>
            <AlertDialogAction
              disabled={deleting}
              onClick={(event) => {
                event.preventDefault();
                void deleteTemplate();
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />}
              Delete template
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function PaginationControls({
  page,
  onPrevious,
  onNext,
}: {
  page: Pagination;
  onPrevious: () => void;
  onNext: () => void;
}) {
  if (page.total <= page.limit) return null;
  const start = page.offset + 1;
  const end = Math.min(page.offset + page.limit, page.total);
  return (
    <div className="flex flex-col gap-2 border-t border-border/60 p-4 text-sm sm:flex-row sm:items-center sm:justify-between">
      <span className="text-muted-foreground">Showing {start}–{end} of {page.total}</span>
      <div className="flex gap-2">
        <Button variant="outline" size="sm" onClick={onPrevious} disabled={page.offset === 0}>Previous</Button>
        <Button variant="outline" size="sm" onClick={onNext} disabled={page.offset + page.limit >= page.total}>Next</Button>
      </div>
    </div>
  );
}
